import { CheckCircle2, Plus, PlusCircle, Trash2 } from 'lucide-react';
import { Controller } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Combobox } from '@/components/ui/combobox';
import { CurrencyInput } from '@/components/ui/currency-input';
import { useBillingConditions } from '@/hooks/commercial/use-billing-conditions';
import type { BrazilianUF } from '@/schemas/commercial/price-table.schema';
import {
  createEmptyBillingCondition,
  createEmptyBillingConditionGroup,
} from '@/utils/commercial/price-table-conditions';

const ICMS_CONTRIBUTOR_OPTIONS: { id: string; text: string }[] = [
  { id: 'true', text: 'Sim' },
  { id: 'false', text: 'Não' },
];

interface BillingConditionsSectionProps {
  ufList: { id: string; text: string }[];
  billToOptions: { id: string; text: string }[];
  onValidate?: () => void;
  disabled?: boolean;
}

export function BillingConditionsSection({
  ufList,
  billToOptions,
  onValidate,
  disabled = false,
}: BillingConditionsSectionProps) {
  const { appendGroup, groupFields, totalConditions } = useBillingConditions();

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-sm">Configurações de Faturamento</h3>

      {groupFields.map((g, gi) => (
        <GroupFields
          key={g.rhfId}
          gi={gi}
          ufList={ufList}
          billToOptions={billToOptions}
          disabled={disabled}
          groupsCount={groupFields.length}
          totalConditions={totalConditions}
        />
      ))}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          disabled={disabled}
          onClick={() => appendGroup(createEmptyBillingConditionGroup())}
        >
          <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
          Novo grupo de condições
        </Button>

        {onValidate && (
          <Button
            type="button"
            size="sm"
            className="h-8 text-xs bg-black hover:bg-gray-800 text-white"
            onClick={onValidate}
            disabled={disabled}
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
            Validar condições
          </Button>
        )}
      </div>
    </div>
  );
}

function GroupFields({
  gi,
  ufList,
  billToOptions,
  disabled,
  groupsCount,
  totalConditions,
}: {
  gi: number;
  ufList: { id: string; text: string }[];
  billToOptions: { id: string; text: string }[];
  disabled?: boolean;
  groupsCount: number;
  totalConditions: number;
}) {
  const { condFields, appendCond, removeCondition, removeGroup, control, getUfOptionsFor } =
    useBillingConditions(gi);

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-500">Grupo {gi + 1}</span>
        {groupsCount > 1 && (
          <Button
            variant="ghost"
            type="button"
            size="sm"
            title="Remover grupo"
            className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
            disabled={disabled}
            onClick={() => removeGroup(gi)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {condFields.map((c, ci) => (
        <div
          key={c.rhfId}
          className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-start"
        >
          {/* Vendas para (multi) */}
          <Controller
            control={control}
            name={`groups.${gi}.conditions.${ci}.salesFor`}
            render={({ field, fieldState: { error } }) => {
              const options = getUfOptionsFor(ufList, field.value);
              return (
                <div className="w-full">
                  <div className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                    Vendas para
                  </div>
                  <Combobox
                    data={options}
                    keyExtractor={(e) => e.id}
                    displayValueGetter={(e) => e.text}
                    type="multiple"
                    disabled={disabled}
                    value={options.filter((uf) =>
                      (field.value ?? []).includes(uf.id as BrazilianUF),
                    )}
                    onOptionChange={(v) => field.onChange(v.map((it) => it.id as BrazilianUF))}
                    placeholder="Selecione"
                  />
                  {error?.message && <p className="text-sm text-red-600 mt-1">{error.message}</p>}
                </div>
              );
            }}
          />

          {/* Contribuinte ICMS (single) */}
          <Controller
            control={control}
            name={`groups.${gi}.conditions.${ci}.icmsContributor`}
            render={({ field }) => {
              const value =
                field.value === true
                  ? [{ id: 'true', text: 'Sim' }]
                  : field.value === false
                    ? [{ id: 'false', text: 'Não' }]
                    : [];
              return (
                <div className="w-full">
                  <div className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                    Contribuinte ICMS
                  </div>
                  <Combobox
                    data={ICMS_CONTRIBUTOR_OPTIONS}
                    keyExtractor={(o) => o.id}
                    displayValueGetter={(o) => o.text}
                    type="single"
                    disabled={disabled}
                    value={value}
                    onOptionChange={(v) => {
                      const id = v[0]?.id;
                      field.onChange(id === 'true' ? true : id === 'false' ? false : null);
                    }}
                    placeholder="Selecione"
                  />
                </div>
              );
            }}
          />

          {/* Limite de faturamento */}
          <Controller
            control={control}
            name={`groups.${gi}.conditions.${ci}.billingLimitInCents`}
            render={({ field, fieldState: { error } }) => (
              <div className="w-full">
                <div className="space-y-2">
                  <div className="block text-sm font-medium leading-6 text-gray-900">
                    Limite de faturamento
                  </div>
                  <CurrencyInput
                    value={field.value ?? '0,00'}
                    onChange={(v) => field.onChange(v)}
                    placeholder="0,00"
                    disabled={disabled}
                    error={error?.message}
                  />
                </div>
              </div>
            )}
          />

          {/* Faturar para (single) */}
          <Controller
            control={control}
            name={`groups.${gi}.conditions.${ci}.toBillFor`}
            render={({ field, fieldState: { error } }) => (
              <div className="w-full">
                <div className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                  Faturar para <span className="text-red-500">*</span>
                </div>
                <Combobox
                  data={billToOptions}
                  keyExtractor={(o) => o.id}
                  displayValueGetter={(o) => o.text}
                  type="single"
                  disabled={disabled}
                  value={
                    field.value
                      ? [
                          {
                            id: field.value,
                            text:
                              billToOptions.find((o) => o.id === field.value)?.text ?? field.value,
                          },
                        ]
                      : []
                  }
                  onOptionChange={(v) => field.onChange(v[0]?.id ?? '')}
                  placeholder="Selecione"
                />
                {error?.message && (
                  <p className="text-sm text-red-600 mt-1 box-border">{error.message}</p>
                )}
              </div>
            )}
          />

          {/* Lixeira (handler atômico: remove a condição, ou o grupo inteiro se for a última dele) */}
          {totalConditions > 1 && (
            <div className="flex h-10 items-center justify-end md:mt-7.5">
              <Button
                variant="outline"
                type="button"
                size="sm"
                title="Remover condição"
                className="h-[36px] w-8 p-0"
                disabled={disabled}
                onClick={() => removeCondition(gi, ci)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      ))}

      <div className="flex gap-2 items-center mt-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-gray-600 hover:text-gray-900 h-7 px-2 text-xs"
          type="button"
          disabled={disabled}
          onClick={() => appendCond(createEmptyBillingCondition())}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Nova condição
        </Button>

        <Controller
          control={control}
          name={`groups.${gi}.priority`}
          render={({ field }) => (
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`priority-${gi}`}
                checked={!!field.value}
                onCheckedChange={() => field.onChange(!field.value)}
                disabled={disabled}
              />
              <label
                htmlFor={`priority-${gi}`}
                className="text-xs font-medium leading-none text-gray-600 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Habilitar prioridade
              </label>
            </div>
          )}
        />
      </div>
    </div>
  );
}
